import * as React from "react";
import StationArrowIcon from "~/components/icons/stationArrowIcon";

type Primitive = string | number;

export type DropdownOption<T> = T;

export type DropdownFieldProps<T> = {
  label?: string;

  options: DropdownOption<T>[];

  value?: T | null;
  defaultValue?: T | null;

  onChange?: (value: T | null) => void;

  getOptionLabel?: (opt: T) => string;
  getOptionValue?: (opt: T) => Primitive;

  placeholder?: string;

  disabled?: boolean;

  error?: string;

  className?: string;
  fieldClassName?: string;
  menuClassName?: string;

  closeOnSelect?: boolean;
};

function useOnClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  handler: () => void
) {
  React.useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      const el = ref.current;
      if (!el) return;
      if (event.target && el.contains(event.target as Node)) return;
      handler();
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

export default function DropdownField<T>(props: DropdownFieldProps<T>) {
  const {
    label,
    options,
    value,
    defaultValue = null,
    onChange,
    getOptionLabel,
    getOptionValue,
    placeholder = "Please Select",
    disabled = false,
    error,
    className,
    fieldClassName,
    menuClassName,
    closeOnSelect = true,
  } = props;

  const [internalValue, setInternalValue] = React.useState<T | null>(
    defaultValue
  );
  const selected = value !== undefined ? value : internalValue;

  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);

  useOnClickOutside(rootRef, () => setOpen(false));

  const labelOf = React.useCallback(
    (opt: T) => {
      if (getOptionLabel) return getOptionLabel(opt);
      return String(opt as unknown as Primitive);
    },
    [getOptionLabel]
  );

  const valueOf = React.useCallback(
    (opt: T) => {
      if (getOptionValue) return getOptionValue(opt);
      return opt as unknown as Primitive;
    },
    [getOptionValue]
  );

  const selectedLabel = selected == null ? "" : labelOf(selected);

  const commit = (next: T | null) => {
    if (value === undefined) setInternalValue(next);
    onChange?.(next);
  };

  const onToggle = () => {
    if (disabled) return;
    setOpen((s) => !s);
  };

  const isOpen = open && !disabled;

  return (
    <div ref={rootRef} className={["relative", className ?? ""].join(" ")}>
      {label ? (
        <div className="mb-2 text-[14px] font-normal leading-5 text-[#99A1AA]">
          {label}
        </div>
      ) : null}

      {/* Field */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseDown={(e) => e.preventDefault()}
        disabled={disabled}
        className={[
          "relative flex h-10 w-full items-center justify-between rounded-[8px] border px-4",
          "bg-transparent",
          error ? "border-red-500" : "border-[#2C313B]",
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
          "outline-none focus:outline-none focus-visible:outline-none",
          "shadow-none",
          "[-webkit-tap-highlight-color:transparent]",
          fieldClassName ?? "",
        ].join(" ")}
      >
        <span
          className={[
            "truncate text-left text-[15px] leading-5",
            selected ? "text-[#99A1AA]" : "text-[#727C92]",
          ].join(" ")}
        >
          {selected ? selectedLabel : placeholder}
        </span>

        <span className="ml-3 grid h-4 w-4 place-items-center">
          <StationArrowIcon
            isOpen={isOpen}
            size={16}
            className={disabled ? "text-[#525A6B]" : "text-[#727C92]"}
          />
        </span>
      </button>

      {/* Menu */}
      {open ? (
        <div
          className={[
            "absolute left-0 top-full z-50 mt-3 w-full",
            "rounded-xl border border-[#2C313B]",
            "bg-[#1A1F2A] shadow-[0_24px_60px_rgba(0,0,0,0.55)]",
            "overflow-hidden",
            menuClassName ?? "",
          ].join(" ")}
        >
          <ul className="max-h-65 overflow-auto p-0 m-0 list-none">
            {options.length === 0 ? (
              <li className="px-4 py-3 text-[16px] text-[#727C92]">
                No options
              </li>
            ) : (
              options.map((opt) => {
                const isSelected =
                  selected != null && valueOf(opt) === valueOf(selected);

                return (
                  <li key={String(valueOf(opt))}>
                    <button
                      type="button"
                      className={[
                        "w-full px-4 py-3 text-left text-[18px] leading-6",
                        "text-white/90",
                        "hover:bg-white/5",
                        "border-0 outline-none focus:outline-none focus-visible:outline-none",
                        "[-webkit-tap-highlight-color:transparent]",
                        isSelected ? "bg-white/5" : "bg-transparent",
                      ].join(" ")}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        commit(opt);
                        if (closeOnSelect) setOpen(false);
                      }}
                    >
                      {labelOf(opt)}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ) : null}

      {/* Error */}
      {error ? (
        <div className="mt-2 text-[12px] leading-4 text-red-400">{error}</div>
      ) : null}
    </div>
  );
}
